import { Box, Button, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import React from 'react';
import UserFetch, { UserPost } from '../../userFetch';
import CreateSubscriptionDialog, { SubscriptionFormData } from './CreateSubscriptionDialog';
import ConfirmDialog from '../../components/ConfirmDialog';

interface Subscription {
  id: number;
  owner: string;
  status: 'active' | 'grace' | 'suspended' | 'expired';
  effective_status: 'active' | 'grace' | 'suspended' | 'expired';
  expiry_date: string | null;
  grace_days: number;
  notes: string | null;
  created: string;
  updated: string;
}

const statusColors: Record<string, 'success' | 'warning' | 'error'> = {
  active: 'success',
  grace: 'warning',
  suspended: 'error',
  expired: 'error'
};

function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = React.useState<Subscription[]>([]);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editData, setEditData] = React.useState<SubscriptionFormData | null>(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState<Subscription | null>(null);

  const fetchSubscriptions = () => {
    UserFetch('/api/subscriptions')
      .then((response) => response.json())
      .then((data: Subscription[]) => setSubscriptions(data));
  };

  React.useEffect(() => {
    fetchSubscriptions();
  }, []);

  const handleSave = (data: SubscriptionFormData) => {
    UserPost('/api/subscriptions', data)
      .then((response) => {
        if (response.ok) {
          setDialogOpen(false);
          setEditData(null);
          fetchSubscriptions();
        }
      });
  };

  const handleDelete = (sub: Subscription) => {
    UserFetch(`/api/subscriptions/${sub.owner}`, { method: 'DELETE' })
      .then((response) => {
        if (response.ok) {
          setDeleteConfirm(null);
          fetchSubscriptions();
        }
      });
  };

  const handleEdit = (sub: Subscription) => {
    setEditData({
      owner: sub.owner,
      status: sub.status,
      expiry_date: sub.expiry_date,
      grace_days: sub.grace_days,
      notes: sub.notes
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditData(null);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Subscriptions</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          size="small"
        >
          Add Subscription
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Owner</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Grace Days</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscriptions.map((sub) => (
              <TableRow key={sub.id}>
                <TableCell>{sub.owner}</TableCell>
                <TableCell>
                  <Chip
                    label={sub.effective_status}
                    color={statusColors[sub.effective_status]}
                    size="small"
                    sx={sub.effective_status === 'expired' ? { bgcolor: '#6d1111', color: '#fff' } : undefined}
                  />
                </TableCell>
                <TableCell>{formatDate(sub.expiry_date)}</TableCell>
                <TableCell>{sub.grace_days}</TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {sub.notes || '-'}
                </TableCell>
                <TableCell>{formatDate(sub.updated)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(sub)}
                    title="Edit subscription"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteConfirm(sub)}
                    title="Delete subscription"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateSubscriptionDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSave}
        editData={editData}
      />

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete Subscription"
        message={`Are you sure you want to delete the subscription for "${deleteConfirm?.owner}"? The device will revert to default active status.`}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </Box>
  );
}

export default SubscriptionsPage;
